import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import GoBack from '../subsidiary/GoBack/GoBack';
import {
  BREATHING_PHASES,
  GROUNDING_STEPS,
  SHOULDER_STEPS,
  STRESS_DISCLAIMER,
} from '../../utils/stressExercises';
import styles from './Pause.module.css';

const TITLES = {
  breathing: 'Дыхание 4-7-8',
  shoulders: 'Разгрузка плеч',
  grounding: 'Заземление 5-4-3-2-1',
};

const MESSAGES = {
  breathing: 'Четыре цикла дыхания завершены. Становится легче?',
  shoulders: 'Плечи расслаблены. Продолжайте в комфортном темпе.',
  grounding: 'Вы вернули внимание в настоящий момент. Хорошая работа.',
};

function SuccessPanel({ exerciseId, onDone }) {
  return (
    <div className={styles.success}>
      <div className={styles.checkMark}>✓</div>
      <div className={styles.successTitle}>Отлично!</div>
      <p className={styles.successText}>{MESSAGES[exerciseId]}</p>
      <button type="button" className={styles.primary} onClick={onDone}>
        Готово
      </button>
    </div>
  );
}

function BreathingExercise({ onComplete }) {
  const [running, setRunning] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(BREATHING_PHASES[0].seconds);
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    if (!running) {
      return undefined;
    }
    const timer = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [running]);

  useEffect(() => {
    if (!running || secondsLeft > 0) {
      return;
    }
    const nextPhase = (phaseIndex + 1) % BREATHING_PHASES.length;
    if (nextPhase === 0) {
      const nextCycle = cycle + 1;
      if (nextCycle >= 4) {
        setRunning(false);
        setCompleted(true);
        return;
      }
      setCycle(nextCycle);
    }
    setPhaseIndex(nextPhase);
    setSecondsLeft(BREATHING_PHASES[nextPhase].seconds);
  }, [cycle, phaseIndex, running, secondsLeft]);

  const phase = BREATHING_PHASES[phaseIndex];

  const stop = () => {
    setRunning(false);
    setPhaseIndex(0);
    setSecondsLeft(BREATHING_PHASES[0].seconds);
    setCycle(0);
  };

  if (completed) {
    return (
      <>
        <h1 className="page-title">{TITLES.breathing}</h1>
        <SuccessPanel exerciseId="breathing" onDone={onComplete} />
        <p className={styles.disclaimer}>{STRESS_DISCLAIMER}</p>
      </>
    );
  }

  return (
    <>
      <h1 className="page-title">{TITLES.breathing}</h1>
      <p className={styles.hint}>{`Цикл ${cycle + 1} · рекомендуется 4 цикла`}</p>
      <div className={styles.circle}>
        <div className={styles.phase}>{phase.label}</div>
        <div className={styles.seconds}>{secondsLeft}</div>
      </div>
      {!running ? (
        <button type="button" className={styles.primary} onClick={() => setRunning(true)}>
          Начать
        </button>
      ) : (
        <button type="button" className={styles.stop} onClick={stop}>
          Остановить
        </button>
      )}
      <p className={styles.disclaimer}>{STRESS_DISCLAIMER}</p>
    </>
  );
}

function StepTimerExercise({ onComplete }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(SHOULDER_STEPS[0].seconds);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!running) {
      return undefined;
    }
    const timer = setInterval(() => {
      setSecondsLeft((s) => {
        if (s > 1) {
          return s - 1;
        }
        setStepIndex((i) => {
          const next = i + 1;
          if (next >= SHOULDER_STEPS.length) {
            setRunning(false);
            return i;
          }
          setSecondsLeft(SHOULDER_STEPS[next].seconds);
          return next;
        });
        return 0;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [running]);

  const step = SHOULDER_STEPS[stepIndex];
  const done = stepIndex >= SHOULDER_STEPS.length - 1 && !running && secondsLeft === 0;

  if (done) {
    return (
      <>
        <h1 className="page-title">{TITLES.shoulders}</h1>
        <SuccessPanel exerciseId="shoulders" onDone={onComplete} />
        <p className={styles.disclaimer}>{STRESS_DISCLAIMER}</p>
      </>
    );
  }

  return (
    <>
      <h1 className="page-title">{TITLES.shoulders}</h1>
      <p className={styles.hint}>{`Шаг ${Math.min(stepIndex + 1, SHOULDER_STEPS.length)} из ${
        SHOULDER_STEPS.length
      }`}</p>
      <h2 className={styles.stepTitle}>{step.title}</h2>
      <p className={styles.hint}>{step.hint}</p>
      <div className={`${styles.seconds} ${styles.timer}`}>
        {secondsLeft}
      </div>
      {!running && !done ? (
        <button
          type="button"
          className={styles.primary}
          onClick={() => {
            setRunning(true);
            setSecondsLeft(SHOULDER_STEPS[stepIndex].seconds);
          }}
        >
          {stepIndex === 0 ? 'Начать' : 'Продолжить'}
        </button>
      ) : null}
      {running ? (
        <button type="button" className={styles.stop} onClick={() => setRunning(false)}>
          Пауза
        </button>
      ) : null}
    </>
  );
}

function GroundingExercise({ onComplete }) {
  const [checked, setChecked] = useState(GROUNDING_STEPS.map(() => false));
  const allDone = checked.every(Boolean);

  return (
    <>
      <h1 className="page-title">{TITLES.grounding}</h1>
      <p className={styles.hint}>Отметьте каждый пункт, когда выполните</p>
      {GROUNDING_STEPS.map((label, index) => (
        <button
          key={label}
          type="button"
          className={`${styles.check} ${checked[index] ? styles.checkDone : ''}`}
          onClick={() =>
            setChecked((prev) => {
              const next = [...prev];
              next[index] = !next[index];
              return next;
            })
          }
        >
          {`${index + 1}. ${label}`}
        </button>
      ))}
      {allDone ? <SuccessPanel exerciseId="grounding" onDone={onComplete} /> : null}
      <p className={styles.disclaimer}>{STRESS_DISCLAIMER}</p>
    </>
  );
}

const PauseExercise = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const onComplete = () => navigate('/pause');

  if (!TITLES[id]) {
    return (
      <section className="main">
        <div className="content-wrapper">
          <p>Упражнение не найдено.</p>
          <Link to="/pause">Назад</Link>
        </div>
      </section>
    );
  }

  return (
    <section className="main">
      <GoBack text="Назад" nav="/pause" />
      <div className="content-wrapper">
        {id === 'breathing' ? <BreathingExercise onComplete={onComplete} /> : null}
        {id === 'shoulders' ? <StepTimerExercise onComplete={onComplete} /> : null}
        {id === 'grounding' ? <GroundingExercise onComplete={onComplete} /> : null}
      </div>
    </section>
  );
};

export default PauseExercise;
